import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

// SECURITY: These are the ONLY emails that can have admin access
// New users will NOT be granted admin access even if they sign up
const ADMIN_EMAILS = ['md.wgtn@gmail.com'];

const AdminPanel = () => {
    const { user } = useAuth();
    const [isCreatingBee, setIsCreatingBee] = useState(false);
    const [beeName, setBeeName] = useState('');
    const [beeDate, setBeeDate] = useState('');
    const [wordsList, setWordsList] = useState('');
    const [createStatus, setCreateStatus] = useState({ message: '', type: '' });

    // Reset progress section state
    const [competitors, setCompetitors] = useState([]);
    const [bees, setBees] = useState([]);
    const [selectedCompetitor, setSelectedCompetitor] = useState('');
    const [selectedBee, setSelectedBee] = useState('');
    const [isResetting, setIsResetting] = useState(false);
    const [resetStatus, setResetStatus] = useState({ message: '', type: '' });

    // Auto-set date to today if not set
    React.useEffect(() => {
        if (!beeDate) {
            const today = new Date().toISOString().split('T')[0];
            setBeeDate(today);
        }
    }, [beeDate]);

    // Load competitors and bees for reset progress section
    React.useEffect(() => {
        const loadData = async () => {
            try {
                // Load competitors
                const competitorsResponse = await apiService.getCompetitors();
                if (competitorsResponse.success && competitorsResponse.competitors) {
                    setCompetitors(competitorsResponse.competitors);
                }

                // Load bees
                const beesResponse = await apiService.getAllBees();
                if (beesResponse.success && beesResponse.bees) {
                    setBees(beesResponse.bees);
                }
            } catch (error) {
                console.error('Failed to load competitors or bees:', error);
            }
        };

        if (isAdmin) {
            loadData();
        }
    }, [isAdmin]);

    // Check if user is admin - must be in hardcoded admin list
    const isAdmin = user && ADMIN_EMAILS.includes(user.email);

    if (!isAdmin) {
        return null; // Don't show admin panel for non-admins
    }

    // Additional security: warn if user doesn't have admin user_type but is in admin emails
    if (isAdmin && user.user_type && user.user_type !== 'admin') {
        console.warn('User in admin emails list but user_type is not admin:', user.email, user.user_type);
    }

    const handleCreateBee = async (e) => {
        e.preventDefault();
        setIsCreatingBee(true);
        setCreateStatus({ message: '', type: '' });

        try {
            // Parse words from textarea (one per line)
            const words = wordsList
                .split('\n')
                .map(word => word.trim())
                .filter(word => word.length > 0);

            if (words.length === 0) {
                throw new Error('Please enter at least one word');
            }

            // Convert date to ISO 8601 format
            let isoDate;
            if (beeDate) {
                // beeDate comes from HTML date input as "YYYY-MM-DD"
                // Convert to ISO 8601 format
                const dateObj = new Date(beeDate + 'T12:00:00.000Z');
                isoDate = dateObj.toISOString();
            } else {
                isoDate = new Date().toISOString();
            }

            const requestData = {
                name: beeName.trim(),
                bee_date: isoDate,
                words: words,
                user_email: user.email
            };


            const response = await apiService.adminCreateBee(requestData);

            if (response.success) {
                setCreateStatus({
                    message: `Successfully created "${beeName}" with ${response.word_count} words!`,
                    type: 'success'
                });
                
                // Clear form
                setBeeName('');
                setBeeDate('');
                setWordsList('');
            } else {
                throw new Error(response.error || 'Failed to create bee');
            }
        } catch (error) {
            setCreateStatus({
                message: error.message || 'Failed to create bee',
                type: 'error'
            });
        } finally {
            setIsCreatingBee(false);
        }
    };

    const handleResetProgress = async (e) => {
        e.preventDefault();
        setIsResetting(true);
        setResetStatus({ message: '', type: '' });

        try {
            const competitor = competitors.find(c => c.competitor_id === selectedCompetitor);
            const bee = bees.find(b => b.bee_id === selectedBee);

            if (!competitor || !bee) {
                throw new Error('Please select both a competitor and a bee');
            }

            const confirmMessage = `Are you sure you want to reset ALL progress for ${competitor.name} in "${bee.name}"? This action cannot be undone and will permanently delete all their practice attempts for this spelling bee.`;

            if (!window.confirm(confirmMessage)) {
                setIsResetting(false);
                return;
            }

            // Call the reset function
            await apiService.resetUserBeeAttempts(selectedCompetitor, selectedBee);

            setResetStatus({
                message: `Successfully reset progress for ${competitor.name} in "${bee.name}"`,
                type: 'success'
            });

            // Clear selections
            setSelectedCompetitor('');
            setSelectedBee('');

        } catch (error) {
            setResetStatus({
                message: error.message || 'Failed to reset progress',
                type: 'error'
            });
        } finally {
            setIsResetting(false);
        }
    };

    return (
        <div className="admin-panel">
            <h2>Admin Panel</h2>
            <p className="admin-notice">
                Welcome, {user.name}! You have admin access.
            </p>

            <div className="admin-section">
                <h3>Create New Spelling Bee</h3>
                <form onSubmit={handleCreateBee} className="create-bee-form">
                    <div className="form-group">
                        <label htmlFor="beeName">
                            Bee Name *
                        </label>
                        <input
                            id="beeName"
                            type="text"
                            value={beeName}
                            onChange={(e) => setBeeName(e.target.value)}
                            placeholder="e.g., 2024 National Spelling Bee"
                            required
                            maxLength={100}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="beeDate">
                            Bee Date
                        </label>
                        <input
                            id="beeDate"
                            type="date"
                            value={beeDate}
                            onChange={(e) => setBeeDate(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="wordsList">
                            Words (one per line) *
                        </label>
                        <textarea
                            id="wordsList"
                            value={wordsList}
                            onChange={(e) => setWordsList(e.target.value)}
                            placeholder="Enter words, one per line:&#10;example&#10;spelling&#10;words&#10;here"
                            rows={10}
                            required
                            style={{ fontFamily: 'monospace' }}
                        />
                        <small className="word-count">
                            {wordsList.split('\n').filter(w => w.trim()).length} words
                        </small>
                    </div>

                    <button 
                        type="submit" 
                        disabled={isCreatingBee || !beeName.trim() || !wordsList.trim()}
                        className="create-bee-button"
                    >
                        {isCreatingBee ? 'Creating Bee...' : 'Create Bee'}
                    </button>
                </form>

                {createStatus.message && (
                    <div className={`status-message ${createStatus.type}`}>
                        {createStatus.message}
                    </div>
                )}
            </div>

            <div className="admin-section reset-section">
                <h3>Reset Competitor Progress</h3>
                <p className="warning-notice">
                    ⚠️ <strong>Warning:</strong> This action permanently deletes all practice attempts for the selected competitor and spelling bee. Use with extreme caution.
                </p>

                <form onSubmit={handleResetProgress} className="reset-form">
                    <div className="form-group">
                        <label htmlFor="resetCompetitor">
                            Select Competitor *
                        </label>
                        <select
                            id="resetCompetitor"
                            value={selectedCompetitor}
                            onChange={(e) => setSelectedCompetitor(e.target.value)}
                            required
                        >
                            <option value="">Choose a competitor...</option>
                            {competitors.map(competitor => (
                                <option key={competitor.competitor_id} value={competitor.competitor_id}>
                                    {competitor.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="resetBee">
                            Select Spelling Bee *
                        </label>
                        <select
                            id="resetBee"
                            value={selectedBee}
                            onChange={(e) => setSelectedBee(e.target.value)}
                            required
                        >
                            <option value="">Choose a spelling bee...</option>
                            {bees.map(bee => (
                                <option key={bee.bee_id} value={bee.bee_id}>
                                    {bee.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="submit"
                        disabled={isResetting || !selectedCompetitor || !selectedBee}
                        className="reset-button"
                    >
                        {isResetting ? 'Resetting Progress...' : 'Reset Progress'}
                    </button>
                </form>

                {resetStatus.message && (
                    <div className={`status-message ${resetStatus.type}`}>
                        {resetStatus.message}
                    </div>
                )}
            </div>

            <style jsx>{`
                .admin-panel {
                    max-width: 800px;
                    margin: 20px auto;
                    padding: 20px;
                    border: 2px solid #e0e0e0;
                    border-radius: 8px;
                    background-color: #f8f9fa;
                }

                .admin-notice {
                    color: #0066cc;
                    font-weight: bold;
                    margin-bottom: 20px;
                    padding: 10px;
                    background-color: #e6f3ff;
                    border-radius: 4px;
                    border: 1px solid #b3d9ff;
                }

                .admin-section {
                    margin-top: 20px;
                }

                .reset-section {
                    border-top: 2px solid #dc3545;
                    padding-top: 20px;
                    margin-top: 40px;
                }

                .warning-notice {
                    background-color: #fff3cd;
                    color: #856404;
                    padding: 12px;
                    border: 1px solid #ffeaa7;
                    border-radius: 4px;
                    margin-bottom: 20px;
                }

                .reset-form {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }

                .create-bee-form {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }

                .form-group label {
                    font-weight: bold;
                    color: #333;
                }

                .form-group input,
                .form-group textarea,
                .form-group select {
                    padding: 8px 12px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                }

                .form-group textarea {
                    resize: vertical;
                    min-height: 200px;
                }

                .word-count {
                    color: #666;
                    font-size: 12px;
                    margin-top: 5px;
                }

                .create-bee-button {
                    padding: 12px 24px;
                    background-color: #28a745;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-size: 16px;
                    font-weight: bold;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .create-bee-button:hover:not(:disabled) {
                    background-color: #218838;
                }

                .create-bee-button:disabled {
                    background-color: #6c757d;
                    cursor: not-allowed;
                }

                .reset-button {
                    padding: 12px 24px;
                    background-color: #dc3545;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-size: 16px;
                    font-weight: bold;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .reset-button:hover:not(:disabled) {
                    background-color: #c82333;
                }

                .reset-button:disabled {
                    background-color: #6c757d;
                    cursor: not-allowed;
                }

                .status-message {
                    margin-top: 15px;
                    padding: 12px;
                    border-radius: 4px;
                    font-weight: bold;
                }

                .status-message.success {
                    background-color: #d4edda;
                    color: #155724;
                    border: 1px solid #c3e6cb;
                }

                .status-message.error {
                    background-color: #f8d7da;
                    color: #721c24;
                    border: 1px solid #f5c6cb;
                }

                h2, h3 {
                    color: #333;
                    margin-bottom: 15px;
                }

                h2 {
                    border-bottom: 2px solid #0066cc;
                    padding-bottom: 10px;
                }
            `}</style>
        </div>
    );
};

export default AdminPanel;